// Locale-aware `Link`: the plain `next/link` emits a locale-LESS path, so
// every card click paid a middleware redirect round-trip.
import { Link } from "@/i18n/routing";
import type { ReactNode } from "react";
import type { LjArticle } from "@/fixtures/lj/fixtures";
import { ArticleCard } from "./article-card";
import { LJ_COLORS, LJ_TYPE } from "@/lib/theme/lj";
import { LoadMoreGrid } from "./load-more-grid";
import { LjMidBanner, PhoneLrec, SidebarAd } from "./listing/sidebar-bits";
import { NativeCardSlot } from "@/components/ads/positions";

/** Underline the headline on hover, same as an editorial card. The creative
 *  lives in its own frame, so the page's `.hkej-tap-title` rule cannot reach it. */
const NATIVE_HOVER_CSS = `
.title { color: #000 !important; text-decoration: none; }
@media (hover: hover) and (pointer: fine) {
  .card:hover .title, a:hover .title, a:hover h3 {
    color: #000 !important;
    text-decoration: underline;
    text-decoration-thickness: 1px;
    text-underline-offset: 4px;
  }
  .card:hover img, a:hover img { opacity: 1 !important; }
}
`;

/** Hidden at mobile (`sm:block`), so nothing is requested there. */

interface Props {
  title: string;
  articles: LjArticle[];
  /** Sidebar LREC for the first article block. */
  adImage?: string;
  /** Sidebar LREC for the second article block. Defaults to `adImage`. */
  adImage2?: string;
  /** Click target for the LREC(s). */
  adHref?: string;
  /** Wide banner creative inserted full-width between the two article blocks. */
  bannerAdImage?: string;
  /** Click target for the banner. */
  bannerAdHref?: string;
  /** Article index AFTER which the banner is inserted. Defaults to 6. */
  bannerAdSplitAt?: number;
  /**
   * First sidebar unit. Half page on the pages that lead with one; the unit
   * after the mid-page banner stays an LREC.
   */
  firstSidebar?: "lrec" | "half";
  /** "View More" CTA target. When omitted, no button renders. */
  viewMoreHref?: string;
  /**
   * Reveal more of `articles` IN PLACE instead of navigating away.
   *
   * The home page's LATEST section sent the reader to `/lj/blog` — a different
   * section entirely. QA: "LATEST section: click on the view more button, will
   * load 12 more articles into the current section". Set the batch size here
   * and pass the full pool as `articles`; `viewMoreHref` is then ignored.
   */
  loadMoreStep?: number;
  /** Label for the View More CTA. */
  viewMoreLabel?: string;
  /** Optional node rendered ABOVE the ad in the FIRST block's sidebar. */
  sidebarTop1?: ReactNode;
  /** Optional node rendered ABOVE the ad in the SECOND block's sidebar. */
  sidebarTop2?: ReactNode;
  /** Phone-only LREC above this section. Home's Latest block leaves it off. */
  phoneLrec?: boolean;
}

/**
 * The grid, with an optional in-place "View More".
 *
 * `LoadMoreGrid` below is the client half — kept separate so a page that does
 * not need the button stays a server component.
 */
export function SummaryGrid({
  title,
  articles,
  adImage,
  adImage2,
  adHref = "#",
  bannerAdImage,
  bannerAdHref = "#",
  bannerAdSplitAt = 6,
  firstSidebar = "lrec",
  viewMoreHref,
  loadMoreStep,
  viewMoreLabel = "View More",
  sidebarTop1,
  sidebarTop2,
  phoneLrec = false,
}: Props) {
  const sidebar2 = adImage2 ?? adImage;

  // In-place "View More" needs client state, so it lives in its own component.
  if (loadMoreStep) {
    return (
      <LoadMoreGrid
        title={title}
        articles={articles}
        adImage={adImage}
        adImage2={adImage2}
        adHref={adHref}
        bannerAdImage={bannerAdImage}
        bannerAdHref={bannerAdHref}
        bannerAdSplitAt={bannerAdSplitAt}
        firstSidebar={firstSidebar}
        loadMoreStep={loadMoreStep}
        viewMoreLabel={viewMoreLabel}
        sidebarTop1={sidebarTop1}
        sidebarTop2={sidebarTop2}
      />
    );
  }

  const first = articles.slice(0, bannerAdSplitAt);
  const rest = articles.slice(bannerAdSplitAt);

  return (
    <section className="mx-auto w-full max-w-[1440px] px-4 lg:px-[120px]">
      {phoneLrec ? <PhoneLrec className="mb-8" /> : null}
      {/* Centered uppercase heading */}
      <header
        className="pt-6 pb-8 text-center"
        style={{ borderTop: `1px solid ${LJ_COLORS.dividerHair}` }}
      >
        <h2
          style={{
            ...LJ_TYPE.sectionTitle,
            fontWeight: 400,
            fontSize: 24,
            lineHeight: "30px",
            letterSpacing: "0.14em",
            color: LJ_COLORS.textPrimary,
          }}
        >
          {title.toUpperCase()}
        </h2>
      </header>

      {/* Block 1 — first 6 articles + sidebar */}
      <ArticleBlock
        articles={first}
        adImage={adImage}
        adHref={adHref}
        sidebarTop={sidebarTop1}
        adAspect={firstSidebar === "half" ? "300 / 600" : "300 / 250"}
      />

      {/* Mobile-only spacer — matches gap-y-12 (48px) between block 1's LREC and block 2's first article */}
      {rest.length > 0 ? <div className="h-12 sm:hidden" aria-hidden /> : null}

      {/* Full-width super banner — hidden at mobile */}
      {bannerAdImage ? (
        <div className="my-12 hidden w-full sm:block lg:my-16">
          <LjMidBanner src={bannerAdImage} href={bannerAdHref} />
        </div>
      ) : null}

      {/* Block 2 — remaining articles + sidebar */}
      {rest.length > 0 ? (
        <ArticleBlock
          articles={rest}
          adImage={sidebar2}
          adHref={adHref}
          keyPrefix="rest"
          sidebarTop={sidebarTop2}
          adAspect="300 / 250"
        />
      ) : null}

      {/* View More CTA */}
      {viewMoreHref ? (
        <div className="mt-14 flex justify-center">
          <Link
            href={viewMoreHref}
            className="inline-flex items-center px-6 py-3 transition-opacity hover:opacity-80 lg:px-10 lg:py-5"
            style={{
              ...LJ_TYPE.navLink,
              fontSize: 12,
              backgroundColor: LJ_COLORS.textPrimary,
              color: LJ_COLORS.textOnDark,
            }}
          >
            {viewMoreLabel}
          </Link>
        </div>
      ) : null}
    </section>
  );
}

/**
 * A half page is desktop-only. Tablet and phone get an LREC in that same
 * rail position; the hidden copy is not requested.
 */
function SidebarUnits({ src, aspectRatio, href }: { src: string; aspectRatio: string; href: string }) {
  const klass = "mx-auto w-full max-w-[300px]";
  if (aspectRatio !== "300 / 600") {
    return <SidebarAd src={src} aspectRatio={aspectRatio} href={href} className={klass} />;
  }
  return (
    <>
      <div className="hidden lg:block">
        <SidebarAd src={src} aspectRatio="300 / 600" href={href} className={klass} />
      </div>
      <div className="lg:hidden">
        <SidebarAd src={src} aspectRatio="300 / 250" href={href} className={klass} />
      </div>
    </>
  );
}

function ArticleBlock({
  articles,
  adImage,
  adHref,
  keyPrefix = "",
  sidebarTop,
  adAspect = "300 / 250",
}: {
  articles: LjArticle[];
  adImage?: string;
  adHref: string;
  keyPrefix?: string;
  sidebarTop?: ReactNode;
  /** Sidebar creative ratio. Taller than 1.6 books a HalfPage. */
  adAspect?: string;
}) {
  const hasSidebar = !!(adImage || sidebarTop);
  return (
    <div
      className={`grid grid-cols-1 gap-x-8 gap-y-12 ${
        hasSidebar ? "md:grid-cols-12" : "lg:grid-cols-12"
      }`}
    >
      <div
        className={`grid content-start grid-cols-1 gap-x-8 gap-y-12 sm:grid-cols-2 ${
          hasSidebar
            ? "md:col-span-8 md:grid-cols-2 lg:col-span-9 lg:grid-cols-3"
            : "md:grid-cols-3 lg:col-span-12 lg:grid-cols-4"
        }`}
      >
        {articles.map((article, i) => {
          const card = <ArticleCard key={`${keyPrefix}-${article.title}-${i}`} article={article} size="lg" />;
          // Every 6th card is the native unit. The card is the fallback.
          if ((i + 1) % 6 !== 0) return card;
          return (
            <NativeCardSlot key={`${keyPrefix}-native-${i}`} className="flex flex-col" showOverflow frameCss={NATIVE_HOVER_CSS}>
              {card}
            </NativeCardSlot>
          );
        })}
      </div>

      {hasSidebar ? (
        <aside className="md:col-span-4 lg:col-span-3">
          {/* The shared offset (nav height + gap), not a hand-written `top-32`
              that drifts whenever the masthead changes. */}
          <div className="flex flex-col gap-8 md:sticky hkej-sticky-offset">
            {sidebarTop}
            {adImage ? <SidebarUnits src={adImage} aspectRatio={adAspect} href={adHref} /> : null}
          </div>
        </aside>
      ) : null}
    </div>
  );
}
